/**
 * Orb palette contract + derivation.
 *
 * The public contract is two brand anchors ({@link OrbPalette}: `base` +
 * `highlight`). From those we derive the full {@link ResolvedOrbPalette} the
 * canvas orb needs — a 4-stop base sphere gradient plus 3 wave color pairs.
 *
 * Derivation strategy ("anchored hue-retarget"): the original Foundry purple
 * palette is treated as a *template*. Every derived color keeps the template
 * stop's lightness / saturation / alpha and only retargets its hue — brighter
 * stops toward `highlight`, darker stops toward `base`, interpolated along the
 * shortest hue arc by the stop's own lightness. This preserves the hand-tuned
 * motion/gloss of the original orb for any brand color, which deriving stops
 * from scratch (mix/darken) does not.
 *
 * All functions here are pure; the resolved palette is passed to the Orb as a
 * prop so multiple widgets hold independent palettes with no shared mutable
 * global state.
 */

/** Two brand anchors for the animated orb. Independent of `accentColor`. */
export interface OrbPalette {
  /** Sphere/body color — drives the darker regions of the orb. */
  base: string;
  /** Highlight/wave color — drives the brighter regions and wave crests. */
  highlight: string;
}

/** A wave's vertical gradient: [crest color, trough color], both with alpha. */
export type WavePair = readonly [string, string];

/** Fully-resolved colors the canvas/SVG orb renders from. */
export interface ResolvedOrbPalette {
  /** 4 stops for the radial base-sphere SVG gradient. */
  readonly base: readonly [string, string, string, string];
  /** 3 wave color pairs (crest→trough), matching the orb's 3 harmonic waves. */
  readonly waves: readonly [WavePair, WavePair, WavePair];
}

// ----- original Foundry palette, as the derivation template -----

const TEMPLATE_BASE = ["#553695", "#8251EE", "#E1CEFC", "#472E79"] as const;
const TEMPLATE_WAVES = [
  ["rgba(225,206,252,0.85)", "rgba(130,81,238,0.4)"],
  ["rgba(243,147,235,0.75)", "rgba(105,60,170,0.35)"],
  ["rgba(183,148,246,0.7)", "rgba(85,54,149,0.3)"],
] as const;

// ----- color math (self-contained; no DOM) -----

interface Rgb {
  r: number;
  g: number;
  b: number;
  a?: number;
}

function parseColor(input: string): Rgb | null {
  const s = input.trim();
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(s);
  if (hex) {
    let h = hex[1]!;
    if (h.length === 3)
      h = h
        .split("")
        .map((c) => c + c)
        .join("");
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
    };
  }
  const rgb = /^rgba?\(([^)]+)\)$/i.exec(s);
  if (rgb) {
    const parts = rgb[1]!.split(",").map((p) => p.trim());
    if (parts.length !== 3 && parts.length !== 4) return null;
    // Reject units (`1px`) and out-of-range values that Number.parseFloat would
    // silently accept; a color channel is a plain number 0..255 or a 0..100%.
    const channel = (v: string): number | null => {
      const m = /^(\d+(?:\.\d+)?)(%?)$/.exec(v);
      if (!m) return null;
      const n = Number(m[1]);
      if (m[2]) return n <= 100 ? (n / 100) * 255 : null;
      return n <= 255 ? n : null;
    };
    const r = channel(parts[0]!);
    const g = channel(parts[1]!);
    const b = channel(parts[2]!);
    if (r === null || g === null || b === null) return null;
    let a: number | undefined;
    if (parts.length === 4) {
      const am = /^(\d+(?:\.\d+)?)$/.exec(parts[3]!);
      if (!am || Number(am[1]) > 1) return null;
      a = Number(am[1]);
    }
    return { r, g, b, a };
  }
  return null;
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;
  const mx = Math.max(r, g, b);
  const mn = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (mx + mn) / 2;
  if (mx !== mn) {
    const d = mx - mn;
    s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
    switch (mx) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return [h * 360, s, l];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  h /= 360;
  if (s === 0) {
    const v = Math.round(l * 255);
    return [v, v, v];
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hue = (t: number): number => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return [
    Math.round(hue(h + 1 / 3) * 255),
    Math.round(hue(h) * 255),
    Math.round(hue(h - 1 / 3) * 255),
  ];
}

function formatColor(r: number, g: number, b: number, a?: number): string {
  return a === undefined ? `rgb(${r},${g},${b})` : `rgba(${r},${g},${b},${a})`;
}

/**
 * Retarget a template color's hue between the two anchors by its lightness,
 * keeping its saturation, lightness, and alpha. `l ≈ 1` → highlight hue;
 * `l ≈ 0` → base hue; interpolated along the shortest hue arc.
 */
function anchorColor(templateColor: string, baseHue: number, highlightHue: number): string {
  const rgb = parseColor(templateColor);
  if (!rgb) return templateColor;
  const [, s, l] = rgbToHsl(rgb.r, rgb.g, rgb.b);
  const dh = ((highlightHue - baseHue + 540) % 360) - 180;
  const h = (baseHue + dh * l + 360) % 360;
  const [nr, ng, nb] = hslToRgb(h, s, l);
  return formatColor(nr, ng, nb, rgb.a);
}

function resolveFromTemplate(baseHue: number, highlightHue: number): ResolvedOrbPalette {
  return {
    base: TEMPLATE_BASE.map((c) => anchorColor(c, baseHue, highlightHue)) as unknown as [
      string,
      string,
      string,
      string,
    ],
    waves: TEMPLATE_WAVES.map(
      (pair) => pair.map((c) => anchorColor(c, baseHue, highlightHue)) as unknown as WavePair,
    ) as unknown as [WavePair, WavePair, WavePair],
  };
}

/** The default Foundry purple orb, precomputed once. Used as the fallback. */
export const DEFAULT_RESOLVED: ResolvedOrbPalette = {
  base: TEMPLATE_BASE.map((c) => {
    const rgb = parseColor(c)!;
    return formatColor(rgb.r, rgb.g, rgb.b);
  }) as unknown as [string, string, string, string],
  waves: TEMPLATE_WAVES.map((pair) => [pair[0], pair[1]] as WavePair) as unknown as [
    WavePair,
    WavePair,
    WavePair,
  ],
};

/**
 * Validate + normalize untrusted palette input. Both `base` and `highlight`
 * must be non-blank, parseable CSS colors. Any invalid/partial input yields
 * `undefined`, which callers map to {@link DEFAULT_RESOLVED} (predictable
 * fallback, never a throw).
 */
export function normalizeOrbPalette(input: unknown): OrbPalette | undefined {
  if (typeof input !== "object" || input === null) return undefined;
  const { base, highlight } = input as Record<string, unknown>;
  if (typeof base !== "string" || typeof highlight !== "string") return undefined;
  if (!parseColor(base) || !parseColor(highlight)) return undefined;
  return { base: base.trim(), highlight: highlight.trim() };
}

/**
 * Derive the full resolved orb palette from the two anchors. Assumes the input
 * is already valid (see {@link normalizeOrbPalette}); pure and deterministic.
 */
export function derivePalette(palette: OrbPalette): ResolvedOrbPalette {
  const base = parseColor(palette.base);
  const highlight = parseColor(palette.highlight);
  if (!base || !highlight) return DEFAULT_RESOLVED;
  const baseHue = rgbToHsl(base.r, base.g, base.b)[0];
  const highlightHue = rgbToHsl(highlight.r, highlight.g, highlight.b)[0];
  return resolveFromTemplate(baseHue, highlightHue);
}

/**
 * Convenience: untrusted input → resolved palette, falling back to the default
 * when the input is invalid/partial. Never throws.
 */
export function resolveOrbPalette(input: unknown): ResolvedOrbPalette {
  const normalized = normalizeOrbPalette(input);
  return normalized ? derivePalette(normalized) : DEFAULT_RESOLVED;
}

/**
 * The orb's CSS drop-shadow color as an `rgba(...)` string derived from a
 * resolved palette (the main sphere stop at low alpha). Fed to `--va-orb-shadow`
 * so the shadow tints with the orb instead of staying fixed purple.
 */
export function orbShadowColor(palette: ResolvedOrbPalette, alpha = 0.35): string {
  const rgb = parseColor(palette.base[1]);
  if (!rgb) return `rgba(109,40,217,${alpha})`;
  return `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
}
